import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  Tooltip,
  ZoomControl,
  useMap
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Clock,
  Crosshair,
  ExternalLink,
  MapPin,
  Smartphone,
  X
} from 'lucide-react';
import { isUsable, mapsHref } from './PunchLocationMap';

/**
 * Punch location as a dialog, not a table-row expand.
 *
 * The register only has room for a place name. Clicking "View map" should open
 * the same thing HR already use on Attendance: who, where, when, how accurate,
 * a pin, and a Google Maps link.
 *
 * WHOSE LOCATION THIS IS is still the caller's job. Pass `caption` and say
 * whether this is the employee's own punch or the supervisor's phone.
 */

const RED_PIN = L.divIcon({
  className: '',
  html: `<span style="
    display:block;width:22px;height:22px;border-radius:50% 50% 50% 0;
    background:#ea4335;transform:rotate(-45deg);
    border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -18]
});

function FitAfterOpen({ points }) {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
      const usable = (points || []).filter((p) => isUsable(p?.lat, p?.lng));
      if (usable.length > 1) {
        map.fitBounds(
          usable.map((p) => [Number(p.lat), Number(p.lng)]),
          { padding: [48, 48], maxZoom: 16 }
        );
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [map, points]);
  return null;
}

const PunchLocationModal = ({
  open,
  onClose,
  title,
  subtitle,
  place,
  eventLabel,
  accuracyM,
  lat,
  lng,
  points = [],
  fence = null,
  overlay = null,
  caption = null
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const usablePoints = (points || []).filter((p) => isUsable(p?.lat, p?.lng));
  const pin = isUsable(lat, lng)
    ? { lat, lng, accuracy_m: accuracyM }
    : usablePoints[0];
  const accuracy = accuracyM ?? pin?.accuracy_m;
  const pinLabel = eventLabel?.split('·')[0]?.trim() || 'Punch';
  // Subtitle already has the project. Don't print the same name again as the
  // place line, and don't float it over the map on top of the zoom buttons.
  const showPlace = place && !alreadyNamed(place, subtitle);
  const mapLabel =
    overlay && !alreadyNamed(overlay, subtitle) && !sameLabel(overlay, place)
      ? overlay
      : null;

  const modal = (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title || 'Location'}</h2>
            {subtitle ? <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2 px-5 pb-3 text-sm text-gray-700">
          {showPlace ? (
            <div className="flex items-center gap-1.5 font-medium">
              <MapPin size={14} className="text-teal-700" />
              {place}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-gray-600">
            {eventLabel ? (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} className="text-gray-400" />
                {eventLabel}
              </span>
            ) : null}
            {Number(accuracy) > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                <Crosshair size={14} className="text-gray-400" />
                ±{Math.round(Number(accuracy))} m accuracy
              </span>
            ) : null}
            {pin ? (
              <span className="font-mono text-xs text-gray-400">
                {Number(pin.lat).toFixed(5)}, {Number(pin.lng).toFixed(5)}
              </span>
            ) : null}
          </div>
          {pin ? (
            <a
              href={mapsHref(pin)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-teal-800 hover:bg-gray-50"
            >
              Open in Google Maps <ExternalLink size={12} />
            </a>
          ) : null}
        </div>

        <MapBody
          points={usablePoints.length ? usablePoints : pin ? [pin] : []}
          fence={fence}
          overlay={mapLabel}
          pinLabel={pinLabel}
        />

        {caption ? (
          <p className="flex items-center gap-2 border-t border-gray-100 px-5 py-3 text-xs text-gray-500">
            <Smartphone size={14} />
            {caption}
          </p>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

function MapBody({ points, fence, overlay, pinLabel }) {
  const usable = (points || []).filter((p) => isUsable(p?.lat, p?.lng));
  if (!usable.length) {
    return (
      <div className="mx-5 mb-4 flex h-[360px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500">
        No location was recorded for this punch.
      </div>
    );
  }

  const centre = [Number(usable[0].lat), Number(usable[0].lng)];

  return (
    <div className="relative mx-5 mb-4 h-[360px] overflow-hidden rounded-xl border border-gray-200">
      <style>{`
        .punch-map-tooltip {
          background: #fff;
          border: none;
          border-radius: 6px;
          box-shadow: 0 2px 8px rgba(0,0,0,.18);
          color: #111827;
          font-weight: 600;
          font-size: 12px;
          padding: 4px 8px;
        }
        .punch-map-tooltip::before {
          border-top-color: #fff !important;
        }
      `}</style>
      {overlay ? (
        <div className="pointer-events-none absolute right-3 top-3 z-[1000] max-w-[70%] truncate rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow">
          {overlay}
        </div>
      ) : null}
      <MapContainer
        key={`${centre[0]},${centre[1]}`}
        center={centre}
        zoom={16}
        scrollWheelZoom={false}
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
      >
        <ZoomControl position="bottomright" />
        <FitAfterOpen points={usable} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {fence && isUsable(fence.lat, fence.lng) && (
          <Circle
            center={[Number(fence.lat), Number(fence.lng)]}
            radius={Number(fence.radius_m) || 200}
            pathOptions={{ color: '#0f766e', weight: 1, fillOpacity: 0.06 }}
          />
        )}
        {usable.map((point, index) => (
          <React.Fragment key={`${point.lat},${point.lng},${index}`}>
            {Number(point.accuracy_m) > 0 && (
              <Circle
                center={[Number(point.lat), Number(point.lng)]}
                radius={Number(point.accuracy_m)}
                pathOptions={{
                  color: '#60a5fa',
                  weight: 1,
                  dashArray: '4 4',
                  fillOpacity: 0.08
                }}
              />
            )}
            <Marker position={[Number(point.lat), Number(point.lng)]} icon={RED_PIN}>
              <Tooltip
                permanent
                direction="top"
                offset={[0, -12]}
                className="punch-map-tooltip"
              >
                {point.label || pinLabel || 'Punch'}
              </Tooltip>
            </Marker>
          </React.Fragment>
        ))}
      </MapContainer>
    </div>
  );
}

function sameLabel(a, b) {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function alreadyNamed(name, subtitle) {
  if (!name || !subtitle) return false;
  return String(subtitle).toLowerCase().includes(String(name).trim().toLowerCase());
}

export default PunchLocationModal;
